/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable @typescript-eslint/no-unused-vars */
import parse from "html-react-parser";
import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import { useTheme } from "react-jss";
import { Dictionary } from "lodash";
import useStyles from "./TextHighlighterRenderer.styles";
import { getRendererProps, RendererProps } from "../BaseRenderer";
import { MentionModel } from "../../../models/MentionModel";
import { VariableModel } from "../../../models/VariableModel";
import { ChoiceModel } from "../../../models/ChoiceModel";
import { VariableToChoiceModel } from "../../../models/VariableToChoiceModel";
import OpenEndRenderer from "./OpenEndRenderer";
import { translate } from "../../../models/utilities/translationUtilities";
import {
    getSettingBoolean,
    getSettingNumber,
    getSettingString,
} from "../../../models/utilities/settingUtilities";
import { isNullOrEmpty } from "../../../utils";
import useTranslatable from "../../../hooks/useTranslatable";
import { MOBILE_WIDTH } from "../../../consts";
import ChoiceListTextHighlighterDropdownRenderer from "./ChoiceListTextHighlighterDropdownRenderer";
import * as Mixins from "../../../theme/Mixins";

interface Props extends RendererProps {
    onHighlight: (e: any) => void;
    highlightedWords: Dictionary<any[]>;
    highlightedWordsByCategories: Dictionary<any[]>;
    showModal: boolean;
    mentions: { [variableName: string]: MentionModel[] };
    onReset(): void;
    onCancelModal: () => void;
    modalPositions: DOMRect;
    onOpenEndChange?(answer: any, event: any): void;
    onOpenEndBlur?(answer: any): void;
    onDeleteHighlight: (shouldSave: boolean) => void;
    selectedChoiceModels: ChoiceModel[];
    isChoiceAlreadyHighlighted: (choice: ChoiceModel, variableName: string) => boolean;
    duplicateWords: string[];
    openEndValue: { [key: string]: any };
    variables: VariableModel[];
    sequencedVariables: VariableModel[];
    toggleIsCategorySelected(isSelected: boolean, enable: boolean): void;
    handleMouseDown: (e: any) => void;
    handleMouseMove: (e: any) => void;
    selectedButtons: string[];
    buttonsRef: any;
    modalRef: any;
    handleTouchStart: (e: any) => void;
    handleTouchMove: (e: any) => void;
    handleTouchEnd: (e: any) => void;
    onHighlightFromButton?: () => void;
    onSaveHighlight?(): void;
    onCancelHighlight?(): void;
    onHideChoiceListRenderer?(): void;
    prefilledCategoryName?: string | null;
    prefilledOpenEndValue?: string | null;
}
export default function TextHighlighterRenderer(props: Props) {
    const [selectedCategory, setSelectedCategory] = useState<ChoiceModel | null>(null);
    const [localOpenEndText, setLocalOpenEndText] = useState<string>("");
    const [hasOpenEndError, setHasOpenEndError] = useState<boolean>(false);
    const [hasCategoryError, setHasCategoryError] = useState<boolean>(false);
    const [highlightButtonClicked, setHighlightButtonClicked] = useState<boolean>(false);
    const [persistentSelectedButtons, setPersistentSelectedButtons] = useState<string[]>([]);
    const [hasAppliedPrefill, setHasAppliedPrefill] = useState<boolean>(false);

    // Create separate ref for mobile modal to avoid conflicts with main container
    const mobileModalButtonsRef = React.useRef<HTMLDivElement>(null);
    const {
        highlightedWords,
        highlightedWordsByCategories,
        onHighlight,
        showModal,
        mentions,
        onReset,
        onCancelModal,
        question,
        questionnaire,
        modalPositions,
        onOpenEndBlur,
        onOpenEndChange,
        onDeleteHighlight,
        selectedChoiceModels,
        isChoiceAlreadyHighlighted,
        duplicateWords,
        language,
        openEndValue,
        variables,
        sequencedVariables,
        toggleIsCategorySelected,
        handleMouseDown,
        handleMouseMove,
        selectedButtons,
        buttonsRef,
        modalRef,
        handleTouchStart,
        handleTouchMove,
        handleTouchEnd,
        prefilledCategoryName,
        prefilledOpenEndValue,
    } = props;

    const isCategorySelectionEnabled = getSettingBoolean(
        question,
        "EnableTextHighlighterCategorySelection",
    );
    function mapVariablesToChoices(variables: VariableModel[]): ChoiceModel[] {
        const choiceList: ChoiceModel[] = [];

        variables.forEach((variable) => {
            const choice = new VariableToChoiceModel(
                variable.elementId,
                variable.index,
                variable.name,
                variable.name,
                true,
                variable.translatedTexts,
                variable.settings.Color,
            );
            choiceList.push(choice);
        });
        return choiceList;
    }

    // Capture selection when it happens and persist it during the highlighting process
    React.useEffect(() => {
        if (selectedButtons.length > 0) {
            // When new text is selected, replace the persistent selection completely
            // This ensures only the latest selection is shown in the modal
            setPersistentSelectedButtons([...selectedButtons]);
        }
        // Don't automatically clear persistent selection when selectedButtons becomes empty
        // Only clear when explicitly reset (e.g., after successful save or cancel)
    }, [selectedButtons]);

    const questionTexts = question.variables[0].choiceList;
    const FormatTemplate = getSettingString(question, "HighlightFormat");

    function getCategoriesColors() {
        const colors: string[] = [];
        variables.forEach((variableItem, index) => {
            colors[index] = getSettingString(variableItem, "Color");
        });
        return colors;
    }

    const categoriesColors: string[] = getCategoriesColors();
    const theme = useTheme();
    const classes = useStyles({
        theme,
        category0Color: categoriesColors[0],
        category1Color: categoriesColors[1],
        category2Color: categoriesColors[2],
        category3Color: categoriesColors[3],
        category4Color: categoriesColors[4],
        category5Color: categoriesColors[5],
    });
    const variable = question.variables[0];
    const choiceList = mapVariablesToChoices(sequencedVariables);
    const [deviceWidth] = useState<number>(window.innerWidth);
    const isMobilePreview = deviceWidth <= MOBILE_WIDTH;

    const isOpenEndEnabled = getSettingBoolean(question, "EnableOpenEndContainer");
    const isOpenEndAnswerMinLength = getSettingNumber(question, "OpenEndMinLength");
    const isOpenEndAnswerMaxLength = getSettingNumber(question, "OpenEndMaxLength");

    // Set CSS custom property for dynamic background color
    React.useEffect(() => {
        if (theme && (theme as any).PrimaryColor) {
            try {
                const dynamicBgColor = Mixins.alpha((theme as any).PrimaryColor, 0.2);
                document.documentElement.style.setProperty(
                    "--highlighter-selection-bg",
                    dynamicBgColor,
                );
            } catch (error) {
                console.warn("Failed to set dynamic highlighter background color:", error);
                // Fallback is handled by CSS var() function
            }
        }
    }, [theme && (theme as any).PrimaryColor]);

    React.useEffect(() => {
        if (!highlightButtonClicked) {
            setHasAppliedPrefill(false);
        }
    }, [highlightButtonClicked]);

    // For mobile modal, ensure that when text is selected within the modal, it's properly captured
    React.useEffect(() => {
        if (isMobilePreview && showModal && selectedButtons.length > 0) {
            // When text is selected in mobile modal, update persistent selection immediately
            setPersistentSelectedButtons([...selectedButtons]);
        }
    }, [isMobilePreview, showModal, selectedButtons]);

    React.useEffect(() => {
        if (!highlightButtonClicked || hasAppliedPrefill) {
            return;
        }

        let applied = false;

        if (isCategorySelectionEnabled) {
            if (prefilledCategoryName) {
                const matchedCategory = choiceList.find(
                    (choice) => choice.name === prefilledCategoryName,
                );
                if (matchedCategory && selectedCategory?.name !== matchedCategory.name) {
                    setSelectedCategory(matchedCategory);
                    setHasCategoryError(false);
                    toggleIsCategorySelected(true, false);
                    applied = true;
                }
            }
        } else if (prefilledCategoryName) {
            toggleIsCategorySelected(true, false);
            applied = true;
        }

        if (isOpenEndEnabled) {
            // Only prefill open-end value if there's a single choice selected
            // For multiple choices, don't prefill to avoid ambiguity
            const shouldPrefillOpenEnd = selectedChoiceModels && selectedChoiceModels.length <= 1;
            if (
                shouldPrefillOpenEnd &&
                prefilledOpenEndValue !== null &&
                prefilledOpenEndValue !== undefined &&
                localOpenEndText !== prefilledOpenEndValue
            ) {
                setLocalOpenEndText(prefilledOpenEndValue);
                setHasOpenEndError(false);
                applied = true;
            } else if (
                selectedChoiceModels &&
                selectedChoiceModels.length > 1 &&
                localOpenEndText !== ""
            ) {
                // Clear open-end text when multiple choices are selected to avoid ambiguity
                setLocalOpenEndText("");
                setHasOpenEndError(false);
                applied = true;
            }
        }

        if (applied) {
            setHasAppliedPrefill(true);
        }
    }, [
        highlightButtonClicked,
        hasAppliedPrefill,
        isCategorySelectionEnabled,
        choiceList,
        prefilledCategoryName,
        prefilledOpenEndValue,
        toggleIsCategorySelected,
        selectedCategory,
        localOpenEndText,
        isOpenEndEnabled,
        selectedChoiceModels,
    ]);

    const handleModalClose = React.useCallback(() => {
        setHighlightButtonClicked(false);
        onCancelModal();
    }, [onCancelModal]);

    // Mobile-specific touch handlers that work with the mobile modal ref
    const handleMobileTouchStart = (e: React.TouchEvent) => {
        if (!isMobilePreview || !showModal) return;

        e.preventDefault();
        const touch = e.touches[0];
        const initialButton = document.elementFromPoint(
            touch.clientX,
            touch.clientY,
        ) as HTMLElement;

        if (
            initialButton instanceof HTMLSpanElement &&
            mobileModalButtonsRef.current?.contains(initialButton)
        ) {
            const getWordPosition = (button: HTMLSpanElement) => {
                const positionStr = button.getAttribute("data-position");
                if (!positionStr) return null;
                const position = parseInt(positionStr, 10);
                return isNaN(position) ? null : position;
            };

            initialButton.setAttribute("data-start-index", String(getWordPosition(initialButton)));
            initialButton.setAttribute("data-selected", "true");
            initialButton.style.backgroundColor = "lightgrey";
        }
    };

    const handleMobileTouchMove = (e: React.TouchEvent) => {
        if (!isMobilePreview || !showModal) return;

        const touch = e.touches[0];
        const currentButton = document.elementFromPoint(
            touch.clientX,
            touch.clientY,
        ) as HTMLElement;

        if (
            currentButton instanceof HTMLSpanElement &&
            mobileModalButtonsRef.current?.contains(currentButton)
        ) {
            e.preventDefault();

            const getWordPosition = (button: HTMLSpanElement) => {
                const positionStr = button.getAttribute("data-position");
                if (!positionStr) return null;
                const position = parseInt(positionStr, 10);
                return isNaN(position) ? null : position;
            };

            const currentButtonIndex = getWordPosition(currentButton);
            const buttons = Array.from(
                mobileModalButtonsRef.current.querySelectorAll("[data-position]"),
            ) as HTMLElement[];

            const startButton = mobileModalButtonsRef.current.querySelector("[data-start-index]");
            const startButtonIndex = startButton
                ? Number(startButton.getAttribute("data-start-index"))
                : null;

            if (startButtonIndex !== null && currentButtonIndex !== null) {
                const startIndex = Math.min(currentButtonIndex, startButtonIndex);
                const endIndex = Math.max(currentButtonIndex, startButtonIndex);

                for (let i = 0; i < buttons.length; i++) {
                    const buttonPosition = getWordPosition(buttons[i]);
                    if (
                        buttonPosition !== null &&
                        buttonPosition >= startIndex &&
                        buttonPosition <= endIndex
                    ) {
                        buttons[i].setAttribute("data-selected", "true");
                        buttons[i].style.backgroundColor = "lightgrey";
                    } else {
                        buttons[i].removeAttribute("data-selected");
                        buttons[i].style.backgroundColor = "";
                    }
                }
            }
        }
    };

    const handleMobileTouchEnd = (e: React.TouchEvent) => {
        if (!isMobilePreview || !showModal) return;

        e.preventDefault();

        if (!mobileModalButtonsRef.current) return;

        const buttons = Array.from(
            mobileModalButtonsRef.current.querySelectorAll("[data-position]"),
        ) as HTMLElement[];

        const selectedButtons = buttons.filter((button) => button.hasAttribute("data-selected"));
        const selectedButtonIds = selectedButtons.map((button) => button.id);

        // Update the selection state with mobile modal selection
        // For mobile modal, we just update the persistent selection without triggering onHighlight
        // The user will need to click confirm to apply the highlighting
        if (selectedButtonIds.length > 0) {
            setPersistentSelectedButtons(selectedButtonIds);
        }

        // Reset visual state
        buttons.forEach((button) => {
            button.style.backgroundColor = "";
            button.removeAttribute("data-selected");
        });

        // Remove start index marker
        const startButton = mobileModalButtonsRef.current.querySelector("[data-start-index]");
        if (startButton) {
            startButton.removeAttribute("data-start-index");
        }
    };

    const isClickEventPropagation = true;
    const isMouseDownEventPropagation = true;
    let modalStyles;

    // Get the category question text for the selected category
    const categoryQuestionText = useTranslatable(selectedCategory, "CategoryQuestion");
    const defaultOpenEndQuestionText = useTranslatable(question, "OpenEndQuestion");
    const confirmText = useTranslatable(question, "Confirm");
    const eraseText = useTranslatable(question, "Erase");
    const cancelText = useTranslatable(question, "Cancel");
    const highlightBtnText = useTranslatable(question, "Highlight");
    const highlightText = useTranslatable(question, "HighlightText");

    // Get the question text to display - either from selected category or default
    const getQuestionText = () => {
        if (!isCategorySelectionEnabled) {
            return defaultOpenEndQuestionText;
        }
        return categoryQuestionText ? categoryQuestionText : defaultOpenEndQuestionText;
    };

    const displayQuestionText = getQuestionText();
    const categoryQuestionTextClassName =
        hasOpenEndError || hasCategoryError
            ? `${classes.categoryQuestionText} ${classes.categoryQuestionTextError}`
            : classes.categoryQuestionText;

    if (showModal) {
        if (isMobilePreview) {
            modalStyles = {
                content: {
                    position: "fixed",
                    top: "0",
                    left: "0",
                    right: "0",
                    bottom: "0",
                    width: "100%",
                    height: "100%",
                    border: "none",
                    borderRadius: "0",
                    padding: "0",
                    margin: "0",
                    background: "#ffffff",
                    overflow: "hidden",
                },
                overlay: {
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                    zIndex: 9999,
                },
            };
        } else {
            modalStyles = {
                content: {
                    top: `${modalPositions?.bottom}px`,
                    left: `${modalPositions?.left}px`,
                    right: "auto",
                    bottom: "auto",
                    transform: "",
                    width: "fit-content",
                    height: "auto",
                    position: "relative",
                    border: "1px solid #DFDFDF",
                    bordeRadius: "20px",
                    outline: "none",
                },
                overlay: {
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    marginRight: "-50%",
                    width: "100%",
                    height: "auto",
                    backgroundColor: "transparent",
                    overflow: "auto",
                    zIndex: 1000,
                },
            };
        }
    }

    function getCurrentHighlightedStyle(choiceValue: string) {
        let highlightColor = "";

        variables.forEach((variableItem) => {
            highlightedWordsByCategories[variableItem.name].forEach((x) => {
                if (x.choiceModel && x.choiceModel.value === choiceValue) {
                    highlightColor = getClassName(variableItem);
                }
            });
        });
        return highlightColor;
    }

    function setStateFromMentions(choice: ChoiceModel) {
        variables.forEach((variableItem) => {
            if (
                isSelected(choice, variableItem.name) &&
                !highlightedWordsByCategories[variableItem.name].some(
                    (item) => choice === item.choiceModel,
                )
            ) {
                highlightedWordsByCategories[variableItem.name].push({
                    highlightedWord: translate(choice, "TEXT", language),
                    choiceModel: choice,
                });
                openEndValue[choice.value] = getOpenEndFromMention(choice, variableItem.name);
            }
        });
    }
    function isSelected(choice: ChoiceModel, variableName: string) {
        return props.mentions[variableName]?.some((mention) => mention.value === choice.value);
    }

    function getOpenEndFromMention(choice: ChoiceModel, variableName: string) {
        return props.mentions[variableName].filter((mention) => mention.value === choice.value)[0]
            .openEnd;
    }

    function getClassName(variableItem: VariableModel) {
        switch (variableItem.index) {
            case 0: {
                return classes.selectedCategory0;
            }
            case 1: {
                return classes.selectedCategory1;
            }
            case 2: {
                return classes.selectedCategory2;
            }
            case 3: {
                return classes.selectedCategory3;
            }
            case 4: {
                return classes.selectedCategory4;
            }
            default: {
                return classes.selectedCategory5;
            }
        }
    }

    // Handle single text selection - supports both workflows (memoized to prevent recreations)
    const handleTextSelection = React.useCallback(
        (e: any, choice: ChoiceModel) => {
            // For mobile, we need to handle text selection differently in the modal context
            if (isMobilePreview && showModal) {
                // In mobile modal, allow touch events to work naturally for text selection
                // Don't prevent default to allow native touch selection behavior
                return;
            }

            // For desktop or mobile outside modal, prevent default
            if (!isMobilePreview) {
                e.preventDefault();
                e.stopPropagation();
            }

            // Let the mouse/touch events handle selection properly
            // The mouse events (handleMouseDown, handleMouseMove, handleMouseUp) and
            // touch events (handleTouchStart, handleTouchMove, handleTouchEnd) will properly
            // update selectedButtons and then call onHighlight when the selection is complete
        },
        [isMobilePreview, showModal],
    );

    function HighlightButton({ choiceValue, choicePositionIndex }) {
        const [hovered, setHovered] = useState(false);
        const [selected, setSelected] = useState(false);

        const activeSelection = React.useMemo(() => {
            // Always use the most current selection available
            // Priority: current selectedButtons > persistent selection > empty
            if (selectedButtons.length > 0) {
                return selectedButtons;
            }

            if (highlightButtonClicked && persistentSelectedButtons.length > 0) {
                return persistentSelectedButtons;
            }

            return selectedButtons; // This will be empty array if no selection
        }, [selectedButtons, highlightButtonClicked, persistentSelectedButtons]);

        const shouldBeSelected = React.useMemo(() => {
            return activeSelection.includes(`text-highlight-choice-${choiceValue}`);
        }, [activeSelection, choiceValue]);

        useEffect(() => {
            // Only update state if it actually changed to prevent unnecessary re-renders
            setSelected((prevSelected) =>
                prevSelected !== shouldBeSelected ? shouldBeSelected : prevSelected,
            );
        }, [shouldBeSelected]);

        const choice = questionTexts.find((item) => item.value === choiceValue);
        if (!choice) return null;
        const choiceText = translate(choice, "TEXT", language);
        if (!choiceText) return null;

        setStateFromMentions(choice);

        // Check if the MakeHighlightableTextStandOut setting is enabled
        const shouldUnderline = getSettingBoolean(question, "EnableMakeHighlightableTextStandOut");

        // Use JSS classes - memoized to prevent unnecessary recalculations
        const textClasses = React.useMemo(
            () =>
                [
                    classes.highlightableText,
                    hovered && classes.highlightableTextHovered,
                    selected &&
                        (shouldUnderline
                            ? classes.highlightableTextSelectedNoIndicators
                            : classes.highlightableTextSelected),
                ]
                    .filter(Boolean)
                    .join(" "),
            [
                classes.highlightableText,
                classes.highlightableTextHovered,
                classes.highlightableTextSelectedNoIndicators,
                classes.highlightableTextSelected,
                hovered,
                selected,
                shouldUnderline,
            ],
        );

        if (variables.length > 0 && getSettingBoolean(choice, "Highlightable")) {
            // Check if text is already highlighted
            const highlightStyle = getCurrentHighlightedStyle(choice.value);
            const isHighlighted = !!highlightStyle;

            // Build className with underline if needed and not highlighted
            const shouldShowUnderline = shouldUnderline && !isHighlighted;
            const className = shouldShowUnderline
                ? `${textClasses} ${classes.underlinedHighlightable} ${highlightStyle || ""}`
                : `${textClasses} ${highlightStyle || ""}`;

            return (
                <span
                    key={`${choiceValue}-${choicePositionIndex}`}
                    id={`text-highlight-choice-${choiceValue}`}
                    data-position={choicePositionIndex}
                    onClick={(e) => handleTextSelection(e, choice)}
                    onKeyPress={(e) => handleTextSelection(e, choice)}
                    onMouseDown={(e) => handleMouseDown(e)}
                    onMouseEnter={(e) => {
                        setHovered(true);
                        handleMouseMove(e);
                    }}
                    onMouseLeave={() => setHovered(false)}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                    role="button"
                    tabIndex={0}
                    className={className.trim()}
                >
                    {choiceText}
                </span>
            );
        }

        return (
            <span
                key={`${choiceValue}-${choicePositionIndex}`}
                id={`text-highlight-choice-${choiceValue}`}
                data-position={choicePositionIndex}
                role="button"
                tabIndex={0}
                style={variables.length > 0 ? {} : { marginRight: 5 }}
                className={
                    shouldUnderline
                        ? classes.NonhighlightableText
                        : classes.NonhighlightableTextUnderlined
                }
            >
                {choiceText}
            </span>
        );
    }

    function parseHighlightFormat(highlightFormat) {
        let choicePositionIndex = 0;

        // Backward compatibility for question created in previous version (6.11)
        // Previous version don't have format, only words with spaces
        if (highlightFormat === null || highlightFormat === "" || highlightFormat === undefined) {
            return questionTexts.map((item) => (
                // eslint-disable-next-line react/jsx-key
                <div className={classes.withoutFormatButtonWrapper}>
                    <HighlightButton
                        choiceValue={item.value}
                        choicePositionIndex={choicePositionIndex++}
                    />
                </div>
            ));
        }

        // eslint-disable-next-line consistent-return
        const replace = (domNode) => {
            if (domNode.attribs && domNode.name === "span" && domNode.attribs.mappedchoicecode) {
                const choiceValue = domNode.attribs.mappedchoicecode;
                return (
                    <HighlightButton
                        choiceValue={choiceValue}
                        choicePositionIndex={choicePositionIndex++}
                    />
                );
            }
        };

        return parse(highlightFormat, { replace });
    }

    // SVG Icons
    const HighlightIcon = () => (
        <svg
            className={classes.actionButtonIcon}
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 18 18"
        >
            <path d="M7.16022 8.37801L13.1335 4.10226L13.9475 4.89317L9.54952 10.6994L7.16022 8.37801ZM4.35418 10.8344L3.6485 11.52C3.30122 11.8574 3.30122 12.4054 3.6485 12.7428L5.05708 14.1114C5.40436 14.4488 5.96834 14.4488 6.31563 14.1114L7.02131 13.4258H9.01332C9.43839 13.4258 9.83568 13.2314 10.0857 12.8994L15.6784 5.52751C15.8312 5.32776 15.9117 5.08482 15.9117 4.83648C15.9117 4.52876 15.7867 4.23183 15.5617 4.01318L14.0392 2.53665C13.8141 2.318 13.5113 2.19653 13.1946 2.19653C12.939 2.19653 12.689 2.27481 12.4834 2.42328L4.89594 7.85433C4.55421 8.09727 4.35418 8.48598 4.35418 8.89628V10.8344ZM2.74556 13.0829L0.995259 14.7835C0.870238 14.905 0.800781 15.0696 0.800781 15.2424V15.3693C0.800781 15.7283 1.09806 16.0171 1.46756 16.0171H3.37345C3.55126 16.0171 3.72073 15.9496 3.84575 15.8282L4.70701 14.9914L2.74556 13.0829ZM7.02131 14.7214C6.6518 14.7214 6.35452 15.0103 6.35452 15.3693C6.35452 15.7283 6.6518 16.0171 7.02131 16.0171H16.134C16.5035 16.0171 16.8008 15.7283 16.8008 15.3693C16.8008 15.0103 16.5035 14.7214 16.134 14.7214H7.02131Z" />
        </svg>
    );

    const EraseIcon = () => (
        <svg
            className={classes.actionButtonIcon}
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 18 18"
        >
            <path d="M8.78099 2.99355L1.39443 10.3801C0.602899 11.1716 0.602899 12.4539 1.39443 13.2455L3.92733 15.7784C4.30726 16.1583 4.82334 16.3704 5.36158 16.3704H8.69551H8.99312H15.7876C16.348 16.3704 16.8008 15.9177 16.8008 15.3573C16.8008 14.7969 16.348 14.3441 15.7876 14.3441H11.8585L15.9966 10.2091C16.7881 9.41761 16.7881 8.13533 15.9966 7.3438L11.6495 2.99355C10.858 2.20202 9.57569 2.20202 8.78416 2.99355H8.78099ZM8.99312 14.3473H8.69551H5.35841L2.82552 11.8144L6.77367 7.86621L11.1239 12.2165L8.99312 14.3473Z" />
        </svg>
    );

    // Button logic for flexible workflow (can highlight with or without text selection)
    const hasSelectedChoice = selectedChoiceModels && selectedChoiceModels.length > 0;
    const hasHighlightedChoice =
        hasSelectedChoice &&
        selectedChoiceModels.some((choice) =>
            variables.some((variable) => isChoiceAlreadyHighlighted(choice, variable.name)),
        );

    // Check if there are ANY highlighted words in the text (not just currently selected ones)
    const hasAnyHighlightedWords = React.useMemo(() => {
        return Object.values(highlightedWordsByCategories).some(
            (categoryWords) => categoryWords && categoryWords.length > 0,
        );
    }, [highlightedWordsByCategories]);

    // Button states:
    const hasCurrentSelection = selectedButtons.length > 0 || persistentSelectedButtons.length > 0;
    const eraseButtonDisabled =
        !hasHighlightedChoice &&
        !hasCurrentSelection &&
        !highlightButtonClicked &&
        !hasAnyHighlightedWords;

    const handleHighlightClick = () => {
        // Set flag to indicate Highlight button was clicked
        setHighlightButtonClicked(true);

        // If category selection is disabled, automatically select the first category (dummy category)
        if (!isCategorySelectionEnabled && choiceList.length > 0) {
            const firstCategory = choiceList[0]; // This will be our dummy category
            setSelectedCategory(firstCategory);
            toggleIsCategorySelected(true, false);
        }

        // For mobile, show the modal immediately when highlight button is clicked
        // This allows users to select text within the modal interface
        if (isMobilePreview) {
            // If there's already a text selection, preserve it in persistent selection
            if (selectedButtons && selectedButtons.length > 0) {
                setPersistentSelectedButtons([...selectedButtons]);
            }

            // Create a synthetic event to trigger the modal
            const syntheticEvent = {
                preventDefault: () => {},
                stopPropagation: () => {},
                target: null,
            };
            onHighlight(syntheticEvent);
            return;
        }

        // If there's already a text selection, process it
        if (hasSelectedChoice) {
            if (!isCategorySelectionEnabled) {
                // If category selection is disabled, check if open-end is enabled
                if (isOpenEndEnabled) {
                    // Show open-end field first, don't apply highlighting immediately
                    // The open-end field will be shown due to highlightButtonClicked being true
                    // User will need to fill open-end and click confirm to apply highlighting
                } else {
                    // If open-end is not enabled, directly apply highlighting
                    const validationResult = validateAndApplyHighlighting();
                    if (validationResult === true && props.onSaveHighlight) {
                        props.onSaveHighlight();
                    }
                    // Reset state after processing
                    setSelectedCategory(null);
                    setHighlightButtonClicked(false);
                    setPersistentSelectedButtons([]);
                }
            } else {
                // If category selection is enabled, show the dropdown for category selection
                const syntheticEvent = {
                    preventDefault: () => {},
                    stopPropagation: () => {},
                    target: null,
                };
                onHighlight(syntheticEvent);
            }
        }
        // If no text selection, fields will show and user can select text after choosing category
    };

    // Modal-specific erase function that only clears selection without closing modal
    const handleModalEraseClick = () => {
        // Clear current selection state but keep modal open
        setPersistentSelectedButtons([]);
        setSelectedCategory(null);
        setLocalOpenEndText("");
        setHasOpenEndError(false);
        setHasCategoryError(false);
        setHighlightButtonClicked(false);

        // Reset selection in the control but don't close modal
        if (props.onCancelHighlight) {
            props.onCancelHighlight();
        }
    };

    const handleEraseClick = () => {
        if (hasHighlightedChoice) {
            // Case 1: There are currently selected highlighted choices - remove them
            onDeleteHighlight(true);
        } else if (hasAnyHighlightedWords) {
            // Case 2: There are highlighted words in the text but no current selection
            // We need to find all highlighted choices and remove them
            const allHighlightedChoices: ChoiceModel[] = [];

            // Collect all highlighted choices from all categories
            Object.values(highlightedWordsByCategories).forEach((categoryWords) => {
                if (categoryWords && categoryWords.length > 0) {
                    categoryWords.forEach((item) => {
                        if (
                            item.choiceModel &&
                            !allHighlightedChoices.some(
                                (choice) => choice.value === item.choiceModel.value,
                            )
                        ) {
                            allHighlightedChoices.push(item.choiceModel);
                        }
                    });
                }
            });

            // Temporarily set the selectedChoiceModels to all highlighted choices so onDeleteHighlight can process them
            if (allHighlightedChoices.length > 0) {
                // We need to trigger the deletion through the parent control
                // Since we can't directly modify selectedChoiceModels (it's a prop), we'll clear the highlights directly
                Object.entries(highlightedWordsByCategories).forEach(([key, value]) => {
                    // Clear all highlights in this category
                    if (mentions[key]) {
                        mentions[key].splice(0); // Clear all mentions
                    }
                    value.splice(0); // Clear all highlighted words
                });

                // Save the changes
                onDeleteHighlight(true);
            }
        }

        // Always reset the current selection state when erase is clicked
        // This allows users to make a new selection after erasing
        setHighlightButtonClicked(false);
        setPersistentSelectedButtons([]);
        setSelectedCategory(null);
        setLocalOpenEndText("");
        setHasOpenEndError(false);
        setHasCategoryError(false);

        // Also call the cancel highlight to reset selection in the control
        if (props.onCancelHighlight) {
            props.onCancelHighlight();
        }

        // Hide the choice list renderer if it's open
        if (props.onHideChoiceListRenderer) {
            props.onHideChoiceListRenderer();
        }
    };

    const validateAndApplyHighlighting = () => {
        // Clear previous errors
        setHasOpenEndError(false);
        setHasCategoryError(false);

        // Validation: Check if category is selected (only if category selection is enabled)
        if (isCategorySelectionEnabled && !selectedCategory) {
            setHasCategoryError(true);
            return false;
        }

        // Get the current selection - use the most up-to-date selection available
        // For mobile modal, prioritize persistentSelectedButtons which captures mobile modal selections
        const currentSelection =
            isMobilePreview && showModal && persistentSelectedButtons.length > 0
                ? persistentSelectedButtons
                : selectedButtons.length > 0
                  ? selectedButtons
                  : highlightButtonClicked && persistentSelectedButtons.length > 0
                    ? persistentSelectedButtons
                    : [];

        // Derive selected choice models from current selection
        let currentSelectedChoiceModels = [];

        // Always derive from button selection first (this handles mobile modal selections)
        if (currentSelection.length > 0) {
            currentSelection.forEach((buttonId) => {
                const selectedChoiceValue = buttonId.split("-")[3];
                const selectedChoice = question.variables[0].choiceList.find(
                    (choice) => choice.value === selectedChoiceValue,
                );
                if (selectedChoice) {
                    currentSelectedChoiceModels.push(selectedChoice);
                }
            });
        } else if (selectedChoiceModels && selectedChoiceModels.length > 0) {
            // Fallback to existing selectedChoiceModels if no button selection
            currentSelectedChoiceModels = selectedChoiceModels;
        }

        // Validation: Check if open end is required and filled
        const isOpenEndRequired = getSettingBoolean(question, "OpenEndAnswerRequired");
        if (
            isOpenEndEnabled &&
            isOpenEndRequired &&
            (!localOpenEndText || localOpenEndText.trim() === "")
        ) {
            setHasOpenEndError(true);
            return false;
        }

        // Validation: Check open end min length
        if (isOpenEndEnabled && localOpenEndText.trim() !== "" && isOpenEndAnswerMinLength > 0) {
            if (localOpenEndText.trim().length < isOpenEndAnswerMinLength) {
                setHasOpenEndError(true);
                return false;
            }
        }

        // Validation: Check open end max length
        if (isOpenEndEnabled && localOpenEndText.trim() !== "" && isOpenEndAnswerMaxLength > 0) {
            if (localOpenEndText.trim().length > isOpenEndAnswerMaxLength) {
                setHasOpenEndError(true);
                return false;
            }
        }

        onDeleteHighlight(false);

        // Save the open end value for all selected choice models
        if (isOpenEndEnabled && localOpenEndText.trim() !== "") {
            currentSelectedChoiceModels.forEach((selectedChoiceModel) => {
                // Save the local open end text to the global state using onOpenEndChange
                const syntheticEvent = {
                    target: { value: localOpenEndText },
                };
                onOpenEndChange(selectedChoiceModel, syntheticEvent);
            });
        }

        // Process the currently selected text choice (single selection)
        currentSelectedChoiceModels.forEach((selectedChoiceModel) => {
            const isOpenEndRequired = getSettingBoolean(question, "OpenEndAnswerRequired");
            if (isOpenEndRequired && isNullOrEmpty(localOpenEndText)) {
                toggleIsCategorySelected(true, true);
            } else {
                toggleIsCategorySelected(true, false);
            }

            // Remove existing highlights for this selectedChoiceModel
            Object.entries(highlightedWordsByCategories).forEach(([key, value]) => {
                if (mentions[key]) {
                    const mentionIndex = mentions[key].findIndex(
                        (item) => selectedChoiceModel.value === item.value,
                    );
                    if (mentionIndex > -1) {
                        mentions[key].splice(mentionIndex, 1);
                    }
                }
                const choiceIndex = value.findIndex(
                    (item) => selectedChoiceModel === item.choiceModel,
                );
                if (choiceIndex > -1) {
                    highlightedWordsByCategories[key].splice(choiceIndex, 1);
                }
            });

            // Create the highlighted word from the selected text choice
            let highlightedWord: string;
            const choiceText = translate(selectedChoiceModel, "TEXT", language);
            if (duplicateWords.includes(choiceText)) {
                highlightedWord = `${choiceText} `;
            } else {
                highlightedWord = choiceText;
            }

            // Add the highlight to the selected category (or first real variable if selection is disabled)
            const targetCategoryName = isCategorySelectionEnabled
                ? selectedCategory.name
                : variables[0].name;
            if (highlightedWordsByCategories[targetCategoryName]) {
                highlightedWordsByCategories[targetCategoryName].push({
                    highlightedWord,
                    choiceModel: selectedChoiceModel,
                });
            }

            // Clear highlighted words
            Object.entries(highlightedWords).forEach(([key, value]) => {
                value.splice(0);
            });
        });

        return true; // Success
    };

    return (
        <>
            <div className={classes.textHighlighterActionsWrapper}>
                {!isMobilePreview && highlightButtonClicked && isCategorySelectionEnabled && (
                    <ChoiceListTextHighlighterDropdownRenderer
                        onReset={onReset}
                        variable={variable}
                        mentions={mentions}
                        choices={
                            isCategorySelectionEnabled
                                ? choiceList.filter((choice) => choice.name !== "dummy")
                                : choiceList
                        }
                        highlightedWordsByCategories={highlightedWordsByCategories}
                        selectedChoiceModels={selectedChoiceModels}
                        highlightedWords={highlightedWords}
                        isChoiceAlreadyHighlighted={isChoiceAlreadyHighlighted}
                        duplicateWords={duplicateWords}
                        openEndValue={openEndValue}
                        toggleIsCategorySelected={toggleIsCategorySelected}
                        onDeleteHighlight={onDeleteHighlight}
                        onSaveHighlight={props.onSaveHighlight}
                        onHideChoiceListRenderer={props.onHideChoiceListRenderer}
                        hasError={hasCategoryError}
                        selectedCategoryName={
                            // Only show selected category if there's a single choice selected
                            // For multiple choices, show "Select category" placeholder
                            selectedChoiceModels && selectedChoiceModels.length > 1
                                ? null
                                : (selectedCategory?.name ?? prefilledCategoryName ?? null)
                        }
                        onClick={(choice) => {
                            // Store the selected choice for later processing
                            setSelectedCategory(choice);
                            setHasCategoryError(false);
                            setHasOpenEndError(false); // Clear open end error when category selected
                            // Set category as selected to enable open end input
                            toggleIsCategorySelected(true, false);
                        }}
                        {...getRendererProps(props)}
                    />
                )}
                <button
                    className={
                        highlightButtonClicked ? classes.actionButtonHidden : classes.actionButton
                    }
                    onClick={handleHighlightClick}
                    type="button"
                >
                    <HighlightIcon />
                    {highlightBtnText}
                </button>
                <button
                    className={
                        eraseButtonDisabled ? classes.actionButtonDisabled : classes.actionButton
                    }
                    disabled={eraseButtonDisabled}
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        // Clear any selected buttons immediately to prevent modal from opening
                        if (props.onCancelHighlight) {
                            props.onCancelHighlight();
                        }
                        handleEraseClick();
                    }}
                    type="button"
                >
                    <EraseIcon />
                    {eraseText}
                </button>
            </div>
            <div
                ref={(el) => {
                    buttonsRef.current = el;
                }}
                className={
                    FormatTemplate === null || FormatTemplate === undefined
                        ? classes.withFormatDisableUserSelection
                        : classes.withoutFormatDisableUserSelection
                }
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
            >
                {parseHighlightFormat(FormatTemplate)}
            </div>
            {!isMobilePreview && highlightButtonClicked && isCategorySelectionEnabled && (
                <>
                    {/* Buttons Container - shown when dropdown is visible */}
                    {highlightButtonClicked && !isOpenEndEnabled && isCategorySelectionEnabled && (
                        <div className={classes.buttonsContainer}>
                            {!isOpenEndEnabled && (
                                <>
                                    <button
                                        className={classes.cancelButtonInline}
                                        onClick={() => {
                                            // Reset all state and cancel
                                            setSelectedCategory(null);
                                            setLocalOpenEndText(""); // Clear local open end text
                                            setHasOpenEndError(false); // Clear error states
                                            setHasCategoryError(false);
                                            setHighlightButtonClicked(false); // Reset highlight button flag
                                            setPersistentSelectedButtons([]); // Clear persistent selection
                                            // Hide the choice list renderer
                                            if (props.onHideChoiceListRenderer) {
                                                props.onHideChoiceListRenderer();
                                            }
                                            // Call the cancel callback if provided to clear selection
                                            if (props.onCancelHighlight) {
                                                props.onCancelHighlight();
                                            }
                                        }}
                                        type="button"
                                    >
                                        {cancelText}
                                    </button>
                                    <button
                                        className={classes.confirmButton}
                                        onClick={() => {
                                            // Validate and apply highlighting first, then save
                                            const validationResult = validateAndApplyHighlighting();
                                            if (validationResult === true) {
                                                // Full validation passed - apply highlighting/erase and close
                                                if (props.onSaveHighlight) {
                                                    props.onSaveHighlight();
                                                }
                                                // Reset all state after successful save
                                                setSelectedCategory(null);
                                                setLocalOpenEndText(""); // Clear local open end text
                                                setHasOpenEndError(false); // Clear error states
                                                setHasCategoryError(false);
                                                setHighlightButtonClicked(false); // Reset highlight button flag
                                                setPersistentSelectedButtons([]); // Clear persistent selection
                                                // Hide the entire container
                                                if (props.onHideChoiceListRenderer) {
                                                    props.onHideChoiceListRenderer();
                                                }
                                            }
                                            // If validation failed (false), errors are shown and fields stay open
                                        }}
                                        type="button"
                                    >
                                        {confirmText}
                                    </button>
                                </>
                            )}
                        </div>
                    )}
                </>
            )}

            {/* Open End Field - shown when enabled and highlight button is clicked */}
            {!isMobilePreview && isOpenEndEnabled && highlightButtonClicked && (
                <div
                    className={
                        hasOpenEndError
                            ? `${classes.openEndContainer} ${classes.openEndContainerError}`
                            : classes.openEndContainer
                    }
                >
                    <div className={categoryQuestionTextClassName}>{displayQuestionText}</div>
                    <div className={classes.openEndRowContainer}>
                        <OpenEndRenderer
                            question={question}
                            questionnaire={questionnaire}
                            theme={undefined}
                            onOpenEndBlur={onOpenEndBlur}
                            value={localOpenEndText}
                            onOpenEndChange={(e) => {
                                // Store in local state, don't save immediately
                                const target = e.target as HTMLTextAreaElement;
                                setLocalOpenEndText(target.value);
                                // Clear error when user starts typing
                                setHasOpenEndError(false);
                            }}
                            language={language}
                            isClickEventPropagation={isClickEventPropagation}
                            isMouseDownEventPropagation={isMouseDownEventPropagation}
                            lines={1}
                        />

                        <button
                            className={classes.confirmIconButton}
                            onClick={() => {
                                // Validate and apply highlighting first, then save
                                const validationResult = validateAndApplyHighlighting();
                                if (validationResult === true) {
                                    // Full validation passed - apply highlighting and close
                                    if (props.onSaveHighlight) {
                                        props.onSaveHighlight();
                                    }
                                    // Reset all state after successful save
                                    setSelectedCategory(null);
                                    setLocalOpenEndText(""); // Clear local open end text
                                    setHasOpenEndError(false); // Clear error states
                                    setHasCategoryError(false);
                                    setHighlightButtonClicked(false); // Reset highlight button flag
                                    setPersistentSelectedButtons([]); // Clear persistent selection
                                    // Hide the entire container
                                    if (props.onHideChoiceListRenderer) {
                                        props.onHideChoiceListRenderer();
                                    }
                                }
                                // If validation failed (false), errors are shown and fields stay open
                            }}
                            type="button"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="18"
                                height="18"
                                viewBox="0 0 18 18"
                                fill="none"
                            >
                                <path
                                    d="M9.267 1.13489C8.82055 0.688435 8.09551 0.688435 7.64905 1.13489L1.93445 6.84949C1.488 7.29595 1.488 8.02099 1.93445 8.46744C2.3809 8.91389 3.10594 8.91389 3.5524 8.46744L7.31689 4.69937V15.6571C7.31689 16.2893 7.82763 16.8 8.45981 16.8C9.09199 16.8 9.60273 16.2893 9.60273 15.6571V4.69937L13.3672 8.46387C13.8137 8.91032 14.5387 8.91032 14.9852 8.46387C15.4316 8.01742 15.4316 7.29237 14.9852 6.84592L9.27057 1.13132L9.267 1.13489Z"
                                    fill="white"
                                />
                            </svg>
                        </button>
                    </div>
                </div>
            )}

            {showModal && isMobilePreview && (
                <Modal
                    ref={modalRef}
                    isOpen={showModal}
                    style={modalStyles}
                    ariaHideApp={false}
                    shouldCloseOnOverlayClick={false}
                    onRequestClose={handleModalClose}
                >
                    <div className={classes.mobileModalContent}>
                        <div className={classes.mobileModalHeader}>
                            <h3 className={classes.mobileModalTitle}>{highlightText}</h3>
                            <button
                                className={classes.mobileModalCloseButton}
                                onClick={handleModalClose}
                                type="button"
                            >
                                ×
                            </button>
                        </div>

                        <div className={classes.mobileModalBody}>
                            {/* Category Selection and Erase Button Row */}
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "12px",
                                    marginBottom: "8px",
                                }}
                            >
                                {/* Category Selection - only show if category selection is enabled */}
                                {isCategorySelectionEnabled && (
                                    <div style={{ flex: 1 }}>
                                        <ChoiceListTextHighlighterDropdownRenderer
                                            onReset={onReset}
                                            variable={variable}
                                            mentions={mentions}
                                            choices={choiceList}
                                            highlightedWordsByCategories={
                                                highlightedWordsByCategories
                                            }
                                            selectedChoiceModels={selectedChoiceModels}
                                            highlightedWords={highlightedWords}
                                            isChoiceAlreadyHighlighted={isChoiceAlreadyHighlighted}
                                            duplicateWords={duplicateWords}
                                            openEndValue={openEndValue}
                                            toggleIsCategorySelected={toggleIsCategorySelected}
                                            onDeleteHighlight={onDeleteHighlight}
                                            onSaveHighlight={props.onSaveHighlight}
                                            onHideChoiceListRenderer={
                                                props.onHideChoiceListRenderer
                                            }
                                            hasError={hasCategoryError}
                                            selectedCategoryName={
                                                // Only show selected category if there's a single choice selected
                                                // For multiple choices, show "Select category" placeholder
                                                selectedChoiceModels &&
                                                selectedChoiceModels.length > 1
                                                    ? null
                                                    : (selectedCategory?.name ??
                                                      prefilledCategoryName ??
                                                      null)
                                            }
                                            onClick={(choice) => {
                                                setSelectedCategory(choice);
                                                setHasCategoryError(false);
                                                setHasOpenEndError(false);
                                                toggleIsCategorySelected(true, false);
                                            }}
                                            {...getRendererProps(props)}
                                        />
                                    </div>
                                )}

                                <button
                                    className={classes.actionButton}
                                    style={{
                                        marginLeft: !isCategorySelectionEnabled
                                            ? "auto"
                                            : undefined,
                                    }}
                                    onClick={handleModalEraseClick}
                                    type="button"
                                >
                                    <EraseIcon />
                                    {eraseText}
                                </button>
                            </div>

                            {/* Highlightable Text Section */}
                            <div className={classes.mobileModalSection}>
                                <div
                                    ref={mobileModalButtonsRef}
                                    className={
                                        FormatTemplate === null || FormatTemplate === undefined
                                            ? classes.withFormatDisableUserSelection
                                            : classes.withoutFormatDisableUserSelection
                                    }
                                    onTouchStart={handleMobileTouchStart}
                                    onTouchMove={handleMobileTouchMove}
                                    onTouchEnd={handleMobileTouchEnd}
                                >
                                    {parseHighlightFormat(FormatTemplate)}
                                </div>
                            </div>

                            {/* Open End Field - show if enabled */}
                            {isOpenEndEnabled && (
                                <div className={classes.mobileModalSection}>
                                    <label className={categoryQuestionTextClassName}>
                                        {displayQuestionText}
                                    </label>
                                    <div className={classes.mobileOpenEndRowContainer}>
                                        <OpenEndRenderer
                                            question={question}
                                            questionnaire={questionnaire}
                                            theme={undefined}
                                            onOpenEndBlur={onOpenEndBlur}
                                            value={localOpenEndText}
                                            onOpenEndChange={(e) => {
                                                const target = e.target as HTMLTextAreaElement;
                                                setLocalOpenEndText(target.value);
                                                setHasOpenEndError(false);
                                            }}
                                            language={language}
                                            isClickEventPropagation={isClickEventPropagation}
                                            isMouseDownEventPropagation={
                                                isMouseDownEventPropagation
                                            }
                                            lines={1}
                                            style={{
                                                borderColor: hasOpenEndError ? "#F0345C" : "",
                                            }}
                                        />
                                        <button
                                            className={classes.mobileConfirmIconButton}
                                            onClick={() => {
                                                const validationResult =
                                                    validateAndApplyHighlighting();
                                                if (validationResult === true) {
                                                    if (props.onSaveHighlight) {
                                                        props.onSaveHighlight();
                                                    }
                                                    // Reset all state
                                                    setSelectedCategory(null);
                                                    setLocalOpenEndText("");
                                                    setHasOpenEndError(false);
                                                    setHasCategoryError(false);
                                                    setHighlightButtonClicked(false);
                                                    setPersistentSelectedButtons([]);
                                                    // Close modal
                                                    handleModalClose();
                                                } else {
                                                    // Validation failed - errors are already set and will be displayed
                                                }
                                            }}
                                            type="button"
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="18"
                                                height="18"
                                                viewBox="0 0 18 18"
                                                fill="none"
                                            >
                                                <path
                                                    d="M9.267 1.13489C8.82055 0.688435 8.09551 0.688435 7.64905 1.13489L1.93445 6.84949C1.488 7.29595 1.488 8.02099 1.93445 8.46744C2.3809 8.91389 3.10594 8.91389 3.5524 8.46744L7.31689 4.69937V15.6571C7.31689 16.2893 7.82763 16.8 8.45981 16.8C9.09199 16.8 9.60273 16.2893 9.60273 15.6571V4.69937L13.3672 8.46387C13.8137 8.91032 14.5387 8.91032 14.9852 8.46387C15.4316 8.01742 15.4316 7.29237 14.9852 6.84592L9.27057 1.13132L9.267 1.13489Z"
                                                    fill="white"
                                                />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Confirm/Cancel Buttons - show when open-end is NOT enabled */}
                            {!isOpenEndEnabled && (
                                <div className={classes.mobileModalSection}>
                                    <div className={classes.mobileModalInlineButtons}>
                                        <button
                                            className={classes.cancelButtonInline}
                                            onClick={() => {
                                                // Reset all state when cancelling
                                                setSelectedCategory(null);
                                                setLocalOpenEndText("");
                                                setHasOpenEndError(false);
                                                setHasCategoryError(false);
                                                setHighlightButtonClicked(false);
                                                setPersistentSelectedButtons([]); // Clear persistent selection
                                                if (props.onCancelHighlight) {
                                                    props.onCancelHighlight();
                                                }
                                                handleModalClose();
                                            }}
                                            type="button"
                                        >
                                            {cancelText}
                                        </button>
                                        <button
                                            className={classes.confirmButton}
                                            onClick={() => {
                                                const validationResult =
                                                    validateAndApplyHighlighting();
                                                if (validationResult === true) {
                                                    if (props.onSaveHighlight) {
                                                        props.onSaveHighlight();
                                                    }
                                                    // Reset all state
                                                    setSelectedCategory(null);
                                                    setLocalOpenEndText("");
                                                    setHasOpenEndError(false);
                                                    setHasCategoryError(false);
                                                    setHighlightButtonClicked(false);
                                                    setPersistentSelectedButtons([]);
                                                    // Close modal
                                                    handleModalClose();
                                                }
                                            }}
                                            type="button"
                                        >
                                            {confirmText}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </Modal>
            )}
        </>
    );
}
